import { marketingBuildRevision } from "./site-revision";

type ReliabilityEvent = "page_view_ok" | "js_error" | "unhandled_rejection" | "api_5xx";

type ReliabilityEndpoint = "partner_inquiries" | "community_campaign" | "community_directory";

const IS_LOCAL_PREVIEW = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
const API_BASE_URL = (import.meta.env.VITE_APP_API_URL ?? (IS_LOCAL_PREVIEW ? "http://127.0.0.1:3001/api" : "/api")).replace(/\/$/, "");
const BEACON_URL = `${API_BASE_URL}/telemetry/beacon`;
const APP_VERSION = marketingBuildRevision(import.meta.env.VITE_BUILD_ID as string | undefined);
const MAX_ERROR_BEACONS = 10;
const STATIC_ROUTES = new Set([
  "/",
  "/about",
  "/accessibility",
  "/communities",
  "/connect",
  "/partners",
  "/partners/community-groups",
  "/partners/housing",
  "/partners/public-agencies",
  "/partners/reuse-organizations",
  "/partners/schools-colleges",
  "/partners/workplaces",
  "/privacy",
  "/sponsors",
  "/terms",
]);

let initialized = false;
let errorBeaconsSent = 0;
const successfulRoutes = new Set<string>();

export function reliabilityRoute(pathname = window.location.pathname): string {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (/^\/c\/[^/]+\/events\/[^/]+\/flyer$/.test(path)) return "/c/:community/events/:event/flyer";
  if (/^\/c\/[^/]+\/events\/[^/]+$/.test(path)) return "/c/:community/events/:event";
  if (/^\/c\/[^/]+\/flyer$/.test(path)) return "/c/:community/flyer";
  if (/^\/c\/[^/]+$/.test(path)) return "/c/:community";
  return STATIC_ROUTES.has(path) ? path : "/not-found";
}

function sendReliabilityBeacon(event: ReliabilityEvent, message?: string): void {
  const payload = JSON.stringify({
    event,
    route: reliabilityRoute(),
    ...(message ? { message: message.slice(0, 64) } : {}),
    appVersion: APP_VERSION,
  });

  try {
    const body = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon?.(BEACON_URL, body)) return;
    void fetch(BEACON_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reliability reporting must never disrupt the website experience.
  }
}

function reportClientFailure(event: "js_error" | "unhandled_rejection", category: string): void {
  if (errorBeaconsSent >= MAX_ERROR_BEACONS) return;
  errorBeaconsSent += 1;
  sendReliabilityBeacon(event, category);
}

export function initReliabilityMonitoring(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener("error", (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement) reportClientFailure("js_error", "asset_image_failure");
    else if (target instanceof HTMLScriptElement) reportClientFailure("js_error", "asset_script_failure");
    else if (target instanceof HTMLLinkElement) reportClientFailure("js_error", "asset_style_failure");
    else reportClientFailure("js_error", "window_error");
  }, true);

  window.addEventListener("unhandledrejection", () => {
    reportClientFailure("unhandled_rejection", "promise_rejection");
  });
}

export function recordPageViewOk(): void {
  const route = reliabilityRoute();
  if (successfulRoutes.has(route)) return;
  successfulRoutes.add(route);
  window.requestAnimationFrame(() => sendReliabilityBeacon("page_view_ok"));
}

export async function reliabilityFetch(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  endpoint: ReliabilityEndpoint,
): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status >= 500) sendReliabilityBeacon("api_5xx", endpoint);
  return response;
}
